import { useState, useEffect } from 'react';
import { 
  Category, 
  CreateCategoryFormData, 
  CategoryWithSubcategories 
} from '../types/category';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  where,
  getDocs,
  getDoc,
  DocumentData
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { usePermissions } from './usePermissions';
import { useAuth } from '../contexts/AuthContext';

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [mainCategories, setMainCategories] = useState<CategoryWithSubcategories[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { hasAccess } = usePermissions();
  const { user } = useAuth();
  const orgId = (user as any)?.organizationId as string | undefined;
  const isSuperAdmin = (user as any)?.role === 'super_admin';

  // Real-time listener for all categories
  useEffect(() => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('products')) {
      setLoading(false);
      return;
    }

    if (!isSuperAdmin && !orgId) { setCategories([]); setLoading(false); return; }
    const q = isSuperAdmin
      ? query(collection(db, 'categories'), orderBy('name', 'asc'))
      : query(collection(db, 'categories'), where('organizationId', '==', orgId), orderBy('name', 'asc'));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const loadedCategories = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as Category[];
        
        setCategories(loadedCategories);

        // Organize into main categories and subcategories
        const mains: Record<string, CategoryWithSubcategories> = {};

        // First, collect all main categories
        loadedCategories
          .filter(cat => cat.parentId === null)
          .forEach(cat => {
            mains[cat.id] = { 
              ...cat, 
              subcategories: [] 
            };
          });

        // Then add subcategories to their parents
        loadedCategories
          .filter(cat => cat.parentId !== null)
          .forEach(cat => {
            if (cat.parentId && mains[cat.parentId]) {
              mains[cat.parentId].subcategories?.push(cat);
            }
          });

        // Convert to array and sort
        const mainCategoriesArray = Object.values(mains).sort((a, b) => 
          a.name.localeCompare(b.name)
        );

        // Sort subcategories
        mainCategoriesArray.forEach(main => {
          if (main.subcategories) {
            main.subcategories.sort((a, b) => a.name.localeCompare(b.name));
          }
        });

        setMainCategories(mainCategoriesArray);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading categories:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, orgId, isSuperAdmin]);

  // Get product count for each category - optimalizált verzió
  const updateCategoryProductCounts = async () => {
    try {
      console.log('Kategória termékszámok számolása kezdődik');
      
      // Lezárjuk az aktuális kategória állapotot, hogy ne okozzon végtelen ciklust
      const currentCategories = [...categories];
      const currentMainCategories = [...mainCategories];
      
      // Készítünk egy kategória számláló objektumot
      const categoryCounts: Record<string, number> = {};
      
      // Inicializáljuk a számlálót minden kategóriához
      currentCategories.forEach(category => {
        categoryCounts[category.id] = 0;
      });
      
      // Egy lekérdezéssel lekérjük az összes AKTÍV terméket
      const productsQuery = isSuperAdmin
        ? query(collection(db, 'products'), where('isActive', '==', true))
        : query(collection(db, 'products'), where('organizationId', '==', orgId || '__none__'), where('isActive', '==', true));
      const productsSnapshot = await getDocs(productsQuery);
      
      console.log(`${productsSnapshot.size} aktív termék feldolgozása kategóriák szerint`);
      
      // Termék ID -> kategória ID halmaz a duplikációk elkerülésére
      const processedProducts: Record<string, Set<string>> = {};
      
      // Feldolgozzuk a termékeket és növeljük a megfelelő kategória számláló értékét
      productsSnapshot.forEach(productDoc => {
        const productId = productDoc.id;
        const productData = productDoc.data();
        
        // Inicializáljuk a termék feldolgozott kategóriáit
        if (!processedProducts[productId]) {
          processedProducts[productId] = new Set();
        }
        
        // Ha van kategória mező
        if (productData.category) {
          // Fő kategória
          if (productData.category.categoryId && 
              categoryCounts[productData.category.categoryId] !== undefined && 
              !processedProducts[productId].has(productData.category.categoryId)) {
            categoryCounts[productData.category.categoryId]++;
            processedProducts[productId].add(productData.category.categoryId);
            
            // Debug a problémás kategóriákhoz
            if (productData.category.categoryId === 'problémás-kategória-id-ide') {
              console.log(`Termék '${productId}' hozzáadva az Audió-Videó kategóriához`);
            }
          }
          
          // Alkategória - új formátum (subCategoryId)
          if (productData.category.subCategoryId && 
              categoryCounts[productData.category.subCategoryId] !== undefined &&
              !processedProducts[productId].has(productData.category.subCategoryId)) {
            categoryCounts[productData.category.subCategoryId]++;
            processedProducts[productId].add(productData.category.subCategoryId);
          }
          
          // Alkategória - régi formátum (categoryId + level2)
          // Ha nincs subCategoryId, de van level2, akkor megkeressük az alkategóriát a név alapján
          if (!productData.category.subCategoryId && 
              productData.category.level2 && 
              productData.category.categoryId) {
            // Megkeressük a főkategóriát
            const parentCategory = currentMainCategories.find(c => c.id === productData.category.categoryId);
            if (parentCategory && parentCategory.subcategories) {
              // Megkeressük az alkategóriát a név alapján
              const subCategory = parentCategory.subcategories.find(sc => sc.name === productData.category.level2);
              if (subCategory && 
                  categoryCounts[subCategory.id] !== undefined &&
                  !processedProducts[productId].has(subCategory.id)) {
                categoryCounts[subCategory.id]++;
                processedProducts[productId].add(subCategory.id);
              }
            }
          }
        }
        
        // Ellenőrizzük a categories tömböt is (alternatív tárolási mód)
        if (productData.categories && Array.isArray(productData.categories)) {
          productData.categories.forEach((catId: string) => {
            if (categoryCounts[catId] !== undefined && 
                !processedProducts[productId].has(catId)) {
              categoryCounts[catId]++;
              processedProducts[productId].add(catId);
              
              // Debug a problémás kategóriákhoz
              if (catId === 'problémás-kategória-id-ide') {
                console.log(`Termék '${productId}' hozzáadva az Audió-Videó kategóriához (tömbből)`);
              }
            }
          });
        }
      });
      
      // Debug: Írjuk ki a legnagyobb kategóriákat
      console.log("Top 5 kategória termékszámokkal:");
      Object.entries(categoryCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .forEach(([catId, count]) => {
          const catName = currentCategories.find(c => c.id === catId)?.name || 'Ismeretlen';
          console.log(`${catName}: ${count} termék`);
        });
      
      // Frissítjük a kategóriák productCount értékét
      const updatedCategories = currentCategories.map(category => ({
        ...category,
        productCount: categoryCounts[category.id] || 0
      }));
      
      // Frissítjük a mainCategories objektumot is
      const updatedMainCategories = currentMainCategories.map(mainCategory => {
        // Frissítjük a főkategória productCount értékét
        const updatedMainCategory = {
          ...mainCategory,
          productCount: categoryCounts[mainCategory.id] || 0
        };
        
        // Frissítjük az alkategóriák productCount értékét
        if (updatedMainCategory.subcategories) {
          updatedMainCategory.subcategories = updatedMainCategory.subcategories.map(subCategory => ({
            ...subCategory,
            productCount: categoryCounts[subCategory.id] || 0
          }));
        }
        
        return updatedMainCategory;
      });
      
      // Beállítjuk az új értékeket
      setCategories(updatedCategories);
      setMainCategories(updatedMainCategories);
      
      console.log('Kategória termékszámok számolása befejeződött');
      return true; // Sikeres futás jelzése
    } catch (error) {
      console.error('Error updating category product counts:', error);
      throw error;
    }
  };

  const createCategory = async (data: CreateCategoryFormData): Promise<string> => {
    try {
      const categoryData: any = {
        organizationId: orgId || null,
        name: data.name,
        parentId: data.parentId,
        isActive: data.isActive,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        fullPath: data.name // Default value
      };

      // If we have a parent ID, get the parent's path
      if (data.parentId) {
        const parentRef = doc(db, 'categories', data.parentId);
        const parentSnap = await getDoc(parentRef);
        if (parentSnap.exists()) {
          const parentData = parentSnap.data();
          // Create the full path by combining parent path and current name
          if (parentData.fullPath) {
            categoryData.fullPath = `${parentData.fullPath} / ${data.name}`;
          }
        }
      }

      const docRef = await addDoc(collection(db, 'categories'), categoryData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  };

  const updateCategory = async (id: string, data: Partial<CreateCategoryFormData>): Promise<void> => {
    try {
      const docRef = doc(db, 'categories', id);
      
      const updateData: any = {
        ...data,
        updatedAt: serverTimestamp()
      };

      // If the parent or name changed, we need to update the path
      if ((data.parentId !== undefined || data.name) && categories) {
        const category = categories.find(c => c.id === id);
        if (category) {
          const parentId = data.parentId !== undefined ? data.parentId : category.parentId;
          const name = data.name || category.name;

          if (parentId) {
            const parentRef = doc(db, 'categories', parentId);
            const parentSnap = await getDoc(parentRef);
            if (parentSnap.exists()) {
              const parentData = parentSnap.data();
              if (parentData.fullPath) {
                updateData.fullPath = `${parentData.fullPath} / ${name}`;
              } else {
                updateData.fullPath = name;
              }
            } else {
              updateData.fullPath = name;
            }
          } else {
            updateData.fullPath = name;
          }
        }
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  };

  const deleteCategory = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'categories', id));
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  return {
    categories,
    mainCategories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    updateCategoryProductCounts
  };
}; 